import { useLoaderData, useSearchParams } from '@remix-run/react'
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
} from '@remix-run/server-runtime'
import { json } from '@remix-run/server-runtime'
import { useMemo } from 'react'

import { MasonryGrid } from '~/components/MasonryGrid'
import { ShopReorderDataProvider } from '~/components/ShopReorder/ShopReorderDataProvider'
import {
  listFullPublishedProductsOfShop,
  listFullUnpublishedProductsOfShop,
} from '~/models/product.server'
import type { ProductAnalytic } from '~/models/product_analytic.server'
import { getProductAnalyticsByProductIds } from '~/models/product_analytic.server'
import { patchShop } from '~/models/shop.server'
import { CreateProductButton } from '~/routes/shop.$shopSlug._layout._index/components/CreateProductButton'
import { ProductCard } from '~/routes/shop.$shopSlug._layout._index/components/ProductCard'
import { ProductsPlaceholder } from '~/routes/shop.$shopSlug._layout._index/components/ProductsPlaceholder'
import { patchShopSchema } from '~/schemas/shop'
import type { ShopPromotions } from '~/schemas/shop'
import { requireShopAndItsOptionalSeller } from '~/services/auth-shop.server'
import { getApexSession } from '~/services/session-apex.server'
import { makeDbNullIfNull } from '~/utils/prisma'

export const loader = async (loaderArgs: LoaderFunctionArgs) => {
  const { shop, seller } = await requireShopAndItsOptionalSeller(
    loaderArgs.request
  )

  const sellerSession = await getApexSession(loaderArgs.request)
  const analyticsEnabled = sellerSession.get('analyticsEnabled')

  const [published, unpublished] = await Promise.all([
    listFullPublishedProductsOfShop(shop.id),
    seller ? listFullUnpublishedProductsOfShop(shop.id) : Promise.resolve([]),
  ])

  const productsAnalytics: {
    published: ProductAnalytic[] | undefined
    unpublished: ProductAnalytic[] | undefined
  } = {
    published: undefined,
    unpublished: undefined,
  }

  if (analyticsEnabled) {
    const analyticsPromises: Promise<unknown>[] = []

    if (published.length > 0) {
      const publishedProductIds = published.map((p) => p.id)
      analyticsPromises.push(
        getProductAnalyticsByProductIds(publishedProductIds).then((result) => {
          productsAnalytics.published = result
        })
      )
    }

    if (seller && unpublished.length > 0) {
      const unpublishedProductIds = unpublished.map((p) => p.id)
      analyticsPromises.push(
        getProductAnalyticsByProductIds(unpublishedProductIds).then(
          (result) => {
            productsAnalytics.unpublished = result
          }
        )
      )
    }

    if (analyticsPromises.length > 0) {
      await Promise.all(analyticsPromises)
    }
  }

  return json({
    seller,
    products: { published, unpublished },
    productsAnalytics,
    analyticsEnabled,
  })
}

// Type guard for PATCH request with promotions
function isPatchPromotionsRequest(
  method: string,
  body: unknown
): body is { promotions: ShopPromotions } {
  return (
    method === 'PATCH' &&
    typeof body === 'object' &&
    body !== null &&
    'promotions' in body
  )
}

export async function action(actionArgs: ActionFunctionArgs) {
  const { shop } = await requireShopAndItsOptionalSeller(actionArgs.request)

  const body = await actionArgs.request.json()

  if (isPatchPromotionsRequest(actionArgs.request.method, body)) {
    // Validate the request body against the schema
    const parsedData = patchShopSchema.parse(body)

    // Update the shop with the validated promotions
    const updatedShop = await patchShop(shop.id, {
      promotions: makeDbNullIfNull(parsedData.promotions),
    })

    return json({ success: true, shop: updatedShop })
  }

  throw new Response(`Method ${actionArgs.request.method} not allowed`, {
    status: 405,
  })
}

export default function ProductsPage() {
  const { seller, products, productsAnalytics } = useLoaderData<typeof loader>()
  const [params] = useSearchParams()

  // No navigation state needed - we're doing client-side filtering
  const status = useMemo(() => {
    if (!seller) return 'published'

    return params.get('filter') === 'unpublished' ? 'unpublished' : 'published'
  }, [params, seller])

  const currentProducts = products[status]

  return (
    <ShopReorderDataProvider type="products" data={currentProducts}>
      {({ products: orderedProducts }) => (
        <section className="mb-12">
          {currentProducts.length ? (
            <MasonryGrid>
              {orderedProducts.map((product, index) => {
                const analytics = productsAnalytics[status]?.find(
                  ({ productId }) => productId === product.id
                )

                return (
                  <ProductCard
                    key={index}
                    product={product}
                    analytics={analytics}
                  />
                )
              })}
            </MasonryGrid>
          ) : (
            <ProductsPlaceholder
              hasProductsInOtherTab={
                products[status === 'published' ? 'unpublished' : 'published']
                  .length > 0
              }
              currentTab={status}
            />
          )}

          {seller && (
            <div className="pointer-events-none sticky bottom-4 z-30 mt-12 mb-4 flex justify-center">
              <CreateProductButton />
            </div>
          )}
        </section>
      )}
    </ShopReorderDataProvider>
  )
}
